import { supabase } from '../config/supabase.js';
export class PatientsService {
    /**
     * Submit eligibility form and check if patient qualifies
     */
    async submitEligibilityForm(data) {
        try {
            // Calculate BMI for eligibility
            const heightInches = (data.height.feet * 12) + data.height.inches;
            const bmi = (data.weight / (heightInches * heightInches)) * 703;
            // Basic eligibility logic
            const eligibilityResult = this.calculateEligibility(data, bmi);
            // Store eligibility submission (before account creation)
            const { data: submission, error } = await supabase
                .from('eligibility_submissions')
                .insert({
                email: data.email,
                treatment_type: data.treatmentType,
                first_name: data.firstName,
                last_name: data.lastName,
                age: data.age,
                height_inches: heightInches,
                weight_pounds: data.weight,
                bmi: Math.round(bmi * 10) / 10,
                medical_history: data.medicalHistory,
                current_medications: data.currentMedications,
                insurance_provider: data.insuranceProvider,
                state: data.state,
                zip_code: data.zipCode,
                eligibility_status: eligibilityResult.eligible ? 'approved' : 'pending',
                requires_review: eligibilityResult.requiresReview
            })
                .select()
                .single();
            if (error) {
                console.error('Error submitting eligibility form:', error);
                return { success: false, error: 'Failed to submit eligibility form' };
            }
            return {
                success: true,
                eligibilityResult,
                tempId: submission.id
            };
        }
        catch (error) {
            console.error('Eligibility submission error:', error);
            return { success: false, error: 'An unexpected error occurred' };
        }
    }
    /**
     * Create patient account after signup (links eligibility to user account)
     */
    async createPatientAccount(userId, eligibilityId) {
        try {
            // Get eligibility data
            const { data: eligibility, error: eligibilityError } = await supabase
                .from('eligibility_submissions')
                .select('*')
                .eq('id', eligibilityId)
                .single();
            if (eligibilityError || !eligibility) {
                return { success: false, error: 'Eligibility submission not found' };
            }
            // Create patient record
            const { data: patient, error: patientError } = await supabase
                .from('patients')
                .insert({
                user_id: userId,
                email: eligibility.email,
                first_name: eligibility.first_name,
                last_name: eligibility.last_name,
                age: eligibility.age,
                height_inches: eligibility.height_inches,
                weight_pounds: eligibility.weight_pounds,
                treatment_type: eligibility.treatment_type,
                medical_history: eligibility.medical_history,
                current_medications: eligibility.current_medications,
                insurance_provider: eligibility.insurance_provider,
                state: eligibility.state,
                zip_code: eligibility.zip_code,
                eligibility_status: eligibility.eligibility_status,
                onboarding_completed: false
            })
                .select()
                .single();
            if (patientError) {
                console.error('Error creating patient account:', patientError);
                return { success: false, error: 'Failed to create patient account' };
            }
            // Mark eligibility as linked
            await supabase
                .from('eligibility_submissions')
                .update({ linked_to_user: userId, linked_at: new Date().toISOString() })
                .eq('id', eligibilityId);
            return { success: true, patient };
        }
        catch (error) {
            console.error('Patient account creation error:', error);
            return { success: false, error: 'An unexpected error occurred' };
        }
    }
    /**
     * Get patient profile by user ID
     */
    async getPatientProfile(userId) {
        try {
            // Get patient by profile_id only (since that's the field that exists)
            const { data: patient, error } = await supabase
                .from('patients')
                .select('*')
                .eq('profile_id', userId)
                .maybeSingle();
            if (error) {
                console.error('Error fetching patient profile:', error);
                return { success: false, error: 'Failed to fetch patient profile' };
            }
            if (!patient) {
                console.error('No patient found for user:', userId);
                return { success: false, error: 'Patient profile not found' };
            }
            return { success: true, patient };
        }
        catch (error) {
            console.error('Patient profile fetch error:', error);
            return { success: false, error: 'An unexpected error occurred' };
        }
    }
    /**
     * Update patient profile
     */
    async updatePatientProfile(userId, updates) {
        try {
            const { data: patient, error } = await supabase
                .from('patients')
                .update({
                ...updates,
                updated_at: new Date().toISOString()
            })
                .eq('user_id', userId)
                .select()
                .single();
            if (error) {
                console.error('Error updating patient profile:', error);
                return { success: false, error: 'Failed to update patient profile' };
            }
            return { success: true, patient };
        }
        catch (error) {
            console.error('Patient profile update error:', error);
            return { success: false, error: 'An unexpected error occurred' };
        }
    }
    /**
     * Complete onboarding process
     */
    async completeOnboarding(userId) {
        try {
            const { error } = await supabase
                .from('patients')
                .update({
                onboarding_completed: true,
                updated_at: new Date().toISOString()
            })
                .eq('user_id', userId);
            if (error) {
                console.error('Error completing onboarding:', error);
                return { success: false, error: 'Failed to complete onboarding' };
            }
            return { success: true };
        }
        catch (error) {
            console.error('Onboarding completion error:', error);
            return { success: false, error: 'An unexpected error occurred' };
        }
    }
    /**
     * Get patient medications with preferences using direct fetch API
     */
    async getPatientMedications(userId) {
        try {
            console.log('🔍 getPatientMedications: Starting with direct fetch API for user:', userId);
            // Get patient record from auth user ID
            const { data: patient, error: patientError } = await supabase
                .from('patients')
                .select('id')
                .eq('profile_id', userId)
                .single();
            if (patientError || !patient) {
                console.error('❌ getPatientMedications: Patient not found for user:', userId, patientError);
                console.error('❌ Full error details:', { patientError, userId });
                return { success: false, error: 'Patient profile not found' };
            }
            console.log('🔍 getPatientMedications: Using patient ID:', patient.id);
            // Debug: verify this is the expected patient
            if (patient.id !== '25383ba9-7900-46ac-9654-7f101a662053') {
                console.warn('⚠️ WARNING: Expected patient 25383ba9-7900-46ac-9654-7f101a662053 but got:', patient.id);
            }
            // Use environment-aware configuration instead of hardcoded local URL
            const { getEnvironmentAwareSupabaseUrl, getEnvironmentAwareSupabaseServiceKey } = await import('../config/supabase.js');
            const baseUrl = getEnvironmentAwareSupabaseUrl();
            const serviceKey = getEnvironmentAwareSupabaseServiceKey();
            const apiUrl = `${baseUrl}/rest/v1/patient_medication_preferences`;
            const headers = {
                'apikey': serviceKey,
                'Authorization': `Bearer ${serviceKey}`,
                'Content-Type': 'application/json'
            };
            const queryParams = new URLSearchParams({
                'patient_id': `eq.${patient.id}`,
                'status': 'in.(approved,pending)',
                'select': '*,medications(id,name,brand_name,generic_name,strength,dosage_form,category,active)'
            });
            console.log('🔍 getPatientMedications: Executing direct fetch API call');
            console.log('🔍 API URL:', `${apiUrl}?${queryParams.toString()}`);
            const response = await fetch(`${apiUrl}?${queryParams.toString()}`, {
                method: 'GET',
                headers: headers
            });
            console.log('🔍 getPatientMedications: Fetch response status:', response.status, response.statusText);
            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ getPatientMedications: Fetch error:', errorText);
                return { success: false, error: `HTTP ${response.status}: ${errorText}` };
            }
            const rawPreferences = await response.json();
            console.log('✅ getPatientMedications: Fetch success. Raw data count:', rawPreferences?.length);
            console.log('✅ getPatientMedications: Raw data sample:', rawPreferences?.slice(0, 3));
            if (!rawPreferences || rawPreferences.length === 0) {
                console.log('ℹ️ No medication preferences found');
                return { success: true, medications: [] };
            }
            console.log('✅ Final medication data:', rawPreferences);
            return { success: true, medications: rawPreferences };
        }
        catch (error) {
            console.error('💥 Patient medications fetch error:', error);
            return { success: false, error: 'An unexpected error occurred' };
        }
    }
    /**
     * Calculate eligibility based on form data
     */
    calculateEligibility(data, bmi) {
        const reasons = [];
        let eligible = true;
        let requiresReview = false;
        let estimatedCost = 0;
        // Age requirements
        if (data.age < 18) {
            eligible = false;
            reasons.push('Must be 18 or older');
        }
        else if (data.age > 65) {
            requiresReview = true;
            reasons.push('Age over 65 requires medical review');
        }
        // BMI requirements for weight management
        if (data.treatmentType === 'weight-management' || data.treatmentType === 'glp1') {
            if (bmi < 27) {
                eligible = false;
                reasons.push('BMI must be 27 or higher for weight management programs');
            }
            else if (bmi >= 27 && bmi < 30) {
                requiresReview = true;
                reasons.push('BMI 27-30 may require additional medical evaluation');
            }
        }
        // State coverage (example logic)
        const coveredStates = ['CA', 'NY', 'TX', 'FL', 'WA', 'OR', 'NV', 'AZ'];
        if (!coveredStates.includes(data.state)) {
            eligible = false;
            reasons.push('Service not currently available in your state');
        }
        // Medication interactions
        const contraindicatedMeds = ['insulin', 'warfarin', 'digoxin'];
        if (data.currentMedications?.some(med => contraindicatedMeds.some(contra => med.toLowerCase().includes(contra)))) {
            requiresReview = true;
            reasons.push('Current medications require medical review');
        }
        // Estimate cost based on treatment type
        const treatmentCosts = {
            'glp1': 299,
            'weight-management': 199,
            'diabetes': 149,
            'other': 99
        };
        estimatedCost = treatmentCosts[data.treatmentType] || 99;
        // If requires review, they're potentially eligible pending review
        if (requiresReview && eligible) {
            reasons.push('Potentially eligible pending medical review');
        }
        const nextSteps = eligible ? [
            'Create your account to continue',
            'Complete medical questionnaire',
            'Schedule consultation with provider'
        ] : [
            'Unfortunately, you do not qualify at this time',
            'Contact support for more information'
        ];
        return {
            eligible: eligible || requiresReview,
            reasons,
            requiresReview,
            estimatedCost,
            nextSteps
        };
    }
}
// Export singleton instance
export const patientsService = new PatientsService();
